import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hierarchy, treemap } from 'd3-hierarchy';
import axios from 'axios';

const COLOR_PALETTE = [
  '#0067b8', '#e63946', '#f4a261', '#2a9d8f', '#9d4edd',
  '#fb6f92', '#06aed5', '#84a98c', '#ee6c4d', '#3a86ff',
];

// Límite máximo que admite el backend (main.py: limit = max(20, min(500, limit))).
// Se pide una sola vez al entrar a la página; el resto (agrupar por ley,
// filtrar a una ley concreta, el slider de Top-N) se hace en el cliente sin
// volver a pedir nada al servidor.
const FETCH_LIMIT = 500;
const DEFAULT_DRILL_LIMIT = 20;

// El treemap se calcula SIEMPRE sobre este lienzo virtual fijo, nunca sobre
// un ancho medido en píxeles reales. El <svg> se declara con este mismo
// viewBox y width="100%": es el propio navegador quien lo escala para que
// quepa en su contenedor, vía CSS/SVG puro, sin ninguna medición en
// JavaScript de por medio. Dos intentos anteriores basados en medir el
// contenedor con window.innerWidth y luego con ResizeObserver + ref callback
// seguían cortándose en producción -- un viewBox no puede "llegar tarde" ni
// fallar por timing, se recalcula en cada pintado del navegador.
const VB_W = 1000;
const VB_H = 550;

const shortLabel = (raw) => {
  if (!raw) return '';
  const m = raw.match(/(\d+(?:\s*bis|ter|quater)?)/i);
  return m ? `Art ${m[1]}` : raw.slice(0, 8);
};

const GraphView = () => {
  const { oppositionName } = useParams();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [selectedLaw, setSelectedLaw] = useState(null);
  const [drillLimit, setDrillLimit] = useState(DEFAULT_DRILL_LIMIT);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`/api/oposicion/${oppositionName}/graph?limit=${FETCH_LIMIT}`);
        setNodes(r.data.nodes.map((n) => n.data));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [oppositionName]);

  const lawColors = useMemo(() => {
    const laws = [...new Set(nodes.map((n) => n.law))].sort();
    const m = {};
    laws.forEach((l, i) => { m[l] = COLOR_PALETTE[i % COLOR_PALETTE.length]; });
    return m;
  }, [nodes]);

  // Vista 1: una caja por ley, tamaño = suma de relevancia de sus artículos.
  const lawBoxes = useMemo(() => {
    const totals = {};
    const counts = {};
    nodes.forEach((n) => {
      totals[n.law] = (totals[n.law] || 0) + Math.max(n.pr, 0.00001);
      counts[n.law] = (counts[n.law] || 0) + 1;
    });
    return Object.entries(totals).map(([law, value]) => ({ law, value, count: counts[law] }));
  }, [nodes]);

  // Vista 2: artículos de la ley seleccionada, top N por relevancia individual.
  const lawArticles = useMemo(() => {
    if (!selectedLaw) return [];
    return nodes
      .filter((n) => n.law === selectedLaw)
      .sort((a, b) => b.pr - a.pr);
  }, [nodes, selectedLaw]);

  const rects = useMemo(() => {
    let root;
    if (!selectedLaw) {
      if (!lawBoxes.length) return [];
      root = {
        name: 'root',
        children: lawBoxes.map((lb) => ({
          name: lb.law, fullLabel: lb.law, law: lb.law, value: lb.value, isLawBox: true, count: lb.count,
        })),
      };
    } else {
      const slice = lawArticles.slice(0, drillLimit);
      if (!slice.length) return [];
      root = {
        name: 'root',
        children: slice.map((it) => ({
          name: shortLabel(it.label), fullLabel: it.label, law: it.law, value: Math.max(it.pr, 0.00001),
        })),
      };
    }
    const root_h = hierarchy(root).sum((d) => d.value || 0).sort((a, b) => b.value - a.value);
    treemap().size([VB_W, VB_H]).paddingInner(2).paddingOuter(3).round(true)(root_h);
    return root_h.leaves();
  }, [selectedLaw, lawBoxes, lawArticles, drillLimit]);

  const openLaw = (law) => {
    setSelectedLaw(law);
    setDrillLimit(DEFAULT_DRILL_LIMIT);
  };

  if (loading && !nodes.length) return <div className="page-loader">Cargando mapa…</div>;

  return (
    <div onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })}>
      {selectedLaw ? (
        <button onClick={() => setSelectedLaw(null)} className="back-link">← Volver a leyes</button>
      ) : (
        <button onClick={() => navigate(`/oposicion/${oppositionName}`)} className="back-link">← Volver</button>
      )}

      <header className="page-header">
        <h1 className="page-title">
          Mapa del temario{selectedLaw ? ` — ${selectedLaw}` : ''}
        </h1>
        <p className="page-subtitle">
          {selectedLaw
            ? `${oppositionName} · Top ${Math.min(drillLimit, lawArticles.length)} de ${lawArticles.length} artículos · el tamaño indica su relevancia`
            : `${oppositionName} · ${lawBoxes.length} leyes · haz clic en una para ver sus artículos`}
        </p>
      </header>

      {selectedLaw && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, fontSize: 13 }}>
          <label htmlFor="limit-slider" style={{ fontWeight: 600 }}>Top:</label>
          <input
            id="limit-slider"
            type="range"
            min="10" max={Math.max(10, lawArticles.length)} step="10"
            value={drillLimit}
            onChange={(e) => setDrillLimit(parseInt(e.target.value, 10))}
            style={{ flex: 1, maxWidth: 300 }}
          />
          <span style={{ minWidth: 90, fontVariantNumeric: 'tabular-nums' }}>
            {Math.min(drillLimit, lawArticles.length)} artículos
          </span>
        </div>
      )}

      <div style={{ background: '#fafafa', borderRadius: 12, border: '1px solid #e1e1e1', padding: 8, overflow: 'hidden', position: 'relative' }}>
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" style={{ display: 'block', height: 'auto' }}>
          {rects.map((d, i) => {
            const w = d.x1 - d.x0;
            const h = d.y1 - d.y0;
            const color = lawColors[d.data.law];
            const showLabel = w > 50 && h > 22;
            const isHover = hovered === d.data;
            return (
              <g
                key={`${d.data.law}-${d.data.fullLabel}-${i}`}
                transform={`translate(${d.x0}, ${d.y0})`}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHovered(d.data)}
                onMouseLeave={() => setHovered(null)}
                onClick={() =>
                  d.data.isLawBox
                    ? openLaw(d.data.law)
                    : navigate(`/article/${encodeURIComponent(d.data.law)}/${encodeURIComponent(d.data.fullLabel)}`)
                }
              >
                <rect
                  width={w}
                  height={h}
                  fill={color}
                  fillOpacity={isHover ? 1 : 0.85}
                  stroke={isHover ? '#1a1a1a' : '#fff'}
                  strokeWidth={isHover ? 2 : 1}
                />
                {showLabel && (
                  <text
                    x={6}
                    y={16}
                    fill="#fff"
                    fontSize={w < 80 ? 10 : 12}
                    fontWeight={600}
                    style={{ pointerEvents: 'none' }}
                  >
                    {d.data.name}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {hovered && (
          <div
            style={{
              position: 'fixed',
              left: mouse.x + 12,
              top: mouse.y + 12,
              background: 'rgba(26,26,26,0.95)',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 12,
              pointerEvents: 'none',
              zIndex: 100,
              maxWidth: 320,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            {hovered.isLawBox ? (
              <>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{hovered.fullLabel}</div>
                <div style={{ opacity: 0.7, fontSize: 11, marginTop: 4 }}>{hovered.count} artículos</div>
                <div style={{ opacity: 0.7, fontSize: 11, marginTop: 2 }}>Click para ver sus artículos</div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{hovered.fullLabel}</div>
                <div style={{ opacity: 0.85 }}>{hovered.law}</div>
                <div style={{ opacity: 0.7, fontSize: 11, marginTop: 4 }}>
                  Relevancia: {hovered.value.toFixed(6)}
                </div>
                <div style={{ opacity: 0.7, fontSize: 11, marginTop: 2 }}>Click para abrir</div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GraphView;
